import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/uiDir/button";
import { Input } from "@/components/uiDir/input";
import { Label } from "@/components/uiDir/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/uiDir/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/uiDir/select";
import { Checkbox } from "@/components/uiDir/checkbox";
import { Alert, AlertDescription } from "@/components/uiDir/alert";
import { toast } from "sonner";
import { signUpUser } from "@/lib/authService";

export default function Signup() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    username: "", 
    studentId: "",
    program: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });
  const [submitStatus, setSubmitStatus] = useState("");

  const programs = [
    { value: "BMMA", label: "BMMA (Multimedia Arts)" },
    { value: "COMSCI", label: "Computer Science" },
    { value: "FILM", label: "Film & Visual Media" },
    { value: "INFOSYSTEM", label: "Information Systems" },
    { value: "ENTREPRENEURSHIP", label: "Entrepreneurship" },
  ];

  const generateUsername = (fullName) => {
    return fullName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 20);
  };

  const validateForm = () => {
    if (!formData.agreeToTerms) {
      setSubmitStatus("Please agree to the terms and conditions");
      toast.error("Please agree to the terms and conditions");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setSubmitStatus("Passwords do not match");
      toast.error("Passwords do not match");
      return false;
    }

    if (formData.password.length < 8) {
      setSubmitStatus("Password must be at least 8 characters");
      toast.error("Password must be at least 8 characters");
      return false;
    }

    if (!formData.email.endsWith('@ciit.edu.ph')) {
      setSubmitStatus("Please use your CIIT email address");
      toast.error("Please use your CIIT email address (@ciit.edu.ph)");
      return false;
    }

    if (!formData.fullName || !formData.username || !formData.studentId || !formData.program) {
      setSubmitStatus("Please fill in all required fields");
      toast.error("Please fill in all required fields");
      return false;
    }

    if (formData.username.length < 3) {
      setSubmitStatus("Username must be at least 3 characters");
      toast.error("Username must be at least 3 characters");
      return false;
    }

    if (!/^[a-z0-9_-]+$/.test(formData.username)) {
      setSubmitStatus("Username can only contain lowercase letters, numbers, hyphens and underscores");
      toast.error("Username can only contain lowercase letters, numbers, hyphens and underscores");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus("");

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Sign up with Supabase Auth
      const { data, error } = await signUpUser({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
            username: formData.username,
            student_id: formData.studentId,
            program: formData.program,
          },
        },
      });

      if (error) {
        if (error.message.includes("already registered") || error.message.includes("already exists")) {
          setSubmitStatus("This email is already registered");
          toast.error("This email is already registered. Please log in instead.");
        } else if (error.message.includes("Password")) {
          setSubmitStatus("" + error.message);
          toast.error(error.message);
        } else {
          setSubmitStatus("" + error.message);
          toast.error(error.message || "Failed to create account");
        }
        setIsLoading(false);
        return;
      }

      if (data?.user) {
        setSubmitStatus("✅ Account created successfully!");
        toast.success("Account created! Please check your email to verify your account.");
        
        setTimeout(() => {
          navigate("/login", { 
            state: { 
              message: "Please check your email to verify your account before logging in." 
            }
          });
        }, 2000);
      }
    } catch (err) {
      console.error("Signup error:", err);
      setSubmitStatus("An unexpected error occurred");
      toast.error("An unexpected error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        <Card className="shadow-xl border-0">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="inline-flex items-center gap-2 text-2xl font-bold">
                <span className="bg-gradient-to-r  bg-clip-text text-primary">
                  PortfolioHub
                </span>
              </div>
            </div>
            <CardTitle className="text-2xl">Create Your Account</CardTitle>
            <CardDescription>
              Join hundreds of CIIT students showcasing their work
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  placeholder="Juan Dela Cruz"
                  value={formData.fullName}
                  onChange={(e) => {
                    setFormData({ ...formData, fullName: e.target.value });
                    // Auto-generate username if username field is empty
                    if (!formData.username) {
                      setFormData(prev => ({
                        ...prev,
                        fullName: e.target.value,
                        username: generateUsername(e.target.value)
                      }));
                    }
                  }}
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  placeholder="juandelacruz"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase() })}
                  disabled={isLoading}
                  required
                />
                <p className="text-xs text-gray-500">
                  Lowercase letters, numbers, hyphens and underscores only
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="juan.delacruz@ciit.edu.ph"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={isLoading}
                  required
                />
                <p className="text-xs text-gray-500">
                  Must be a valid CIIT email address
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="studentId">Student ID</Label>
                <Input
                  id="studentId"
                  placeholder="2024-001234"
                  value={formData.studentId}
                  onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="program">Program</Label>
                <Select
                  value={formData.program}
                  onValueChange={(value) => setFormData({ ...formData, program: value })}
                  disabled={isLoading}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your program" />
                  </SelectTrigger>
                  <SelectContent>
                    {programs.map((program) => (
                      <SelectItem key={program.value} value={program.value}>
                        {program.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Min. 8 characters"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter your password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="flex items-start gap-2">
                <Checkbox
                  id="terms"
                  checked={formData.agreeToTerms}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, agreeToTerms: checked as boolean })
                  }
                  disabled={isLoading}
                />
                <label
                  htmlFor="terms"
                  className="text-sm text-gray-600 leading-tight cursor-pointer"
                >
                  I agree to the{" "}
                  <span className="text-purple-600 hover:underline">
                    Terms of Service
                  </span>{" "}
                  and{" "}
                  <span className="text-purple-600 hover:underline">
                    Privacy Policy
                  </span>
                </label>
              </div>

              {submitStatus && (
                <Alert className={submitStatus.includes('✅') ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
                  <AlertDescription className="text-sm">
                    {submitStatus}
                  </AlertDescription>
                </Alert>
              )}

              <Button 
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white flex items-center justify-center gap-2"
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  "Create Account"
                )}
              </Button>

              <p className="text-center text-sm text-gray-600">
                Already have an account?{" "}
                <Link to="/login" className="text-purple-600 font-medium hover:underline">
                  Log in
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}